/**
 * Estado da camada de radar (Zustand). Registra o codec de URL `e` (de "eco")
 * no import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { Manifesto } from './api';
import { isProduto, type Produto } from './produtos';

/** A camada começa desligada, como as outras camadas extras. */
export const DEFAULT_VISIVEL = false;
export const DEFAULT_PRODUTO: Produto = 'dbz';
/**
 * 85%, e não 100%: o eco fraco (azul) cobre áreas grandes, e um pouco de
 * transparência deixa ver a nuvem do satélite embaixo — que é justamente o
 * que se quer comparar.
 */
export const DEFAULT_OPACIDADE = 0.85;
export const LIMITES_OPACIDADE = { min: 0.2, max: 1, step: 0.05 } as const;

export type EstadoCarga = 'idle' | 'loading' | 'ready' | 'error';

export interface Situacao {
  estado: EstadoCarga;
  erro: string | null;
}

/**
 * Situação de cada radar no quadro exibido:
 * `ok` na tela · `carregando` baixando (fica a anterior) · `fora` fora da
 * vista (baixado, mas só é reprojetado quando entra na tela) · `sem` nenhuma
 * varredura a até 12 min deste horário · `falhou` a imagem não veio ·
 * `semDados` o SIPAM não publica este radar.
 */
export type EstadoRadar = 'ok' | 'carregando' | 'fora' | 'sem' | 'falhou' | 'semDados';

export interface RadarNoQuadro {
  estado: EstadoRadar;
  /** Instante da varredura na tela (ou pedida), epoch ms. */
  varredura: number | null;
}

export interface Progresso {
  baixadas: number;
  total: number;
  falhas: number;
}

export interface RadarState {
  visivel: boolean;
  produto: Produto;
  opacidade: number;
  situacao: Situacao;
  manifesto: Manifesto | null;
  /** Por radar (id), no quadro exibido. */
  noQuadro: Record<string, RadarNoQuadro>;
  progresso: Progresso;

  setVisivel: (on: boolean) => void;
  setProduto: (p: Produto) => void;
  setOpacidade: (o: number) => void;
  setSituacao: (s: Situacao) => void;
  setManifesto: (m: Manifesto) => void;
}

export const useRadarStore = create<RadarState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  produto: DEFAULT_PRODUTO,
  opacidade: DEFAULT_OPACIDADE,
  situacao: { estado: 'idle', erro: null },
  manifesto: null,
  noQuadro: {},
  progresso: { baixadas: 0, total: 0, falhas: 0 },

  setVisivel: (visivel) => set({ visivel }),
  setProduto: (produto) => set({ produto }),
  setOpacidade: (opacidade) => set({ opacidade }),
  setSituacao: (situacao) => set({ situacao }),
  setManifesto: (manifesto) => set({ manifesto }),
}));

/**
 * `e=1` liga a camada; depois só o que difere do padrão: `c` = chuva (mm/h)
 * no lugar da refletividade, `o0.6` = opacidade. Ex.: `e=1co0.6`.
 */
export function encodeRadar(s: Pick<RadarState, 'visivel' | 'produto' | 'opacidade'>): string | null {
  if (!s.visivel) return null;
  let out = '1';
  if (s.produto !== DEFAULT_PRODUTO) out += s.produto === 'rate' ? 'c' : '';
  if (s.opacidade !== DEFAULT_OPACIDADE) out += `o${s.opacidade}`;
  return out;
}

export function decodeRadar(value: string): Partial<Pick<RadarState, 'visivel' | 'produto' | 'opacidade'>> | null {
  if (!value || value[0] !== '1') return null;
  const out: Partial<Pick<RadarState, 'visivel' | 'produto' | 'opacidade'>> = { visivel: true };
  const semOpacidade = value.replace(/o\d*\.?\d+/, '');
  const produto = semOpacidade.includes('c') ? 'rate' : DEFAULT_PRODUTO;
  if (isProduto(produto)) out.produto = produto;
  const o = /o(\d*\.?\d+)/.exec(value);
  if (o) {
    const n = Number(o[1]);
    out.opacidade = Number.isFinite(n)
      ? Math.min(LIMITES_OPACIDADE.max, Math.max(LIMITES_OPACIDADE.min, n))
      : DEFAULT_OPACIDADE;
  }
  return out;
}

registerUrlCodec({
  key: 'e',
  encode: () => encodeRadar(useRadarStore.getState()),
  decode: (value) => {
    const s = decodeRadar(value);
    if (s) useRadarStore.setState(s);
  },
  subscribe: (onChange) =>
    useRadarStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.produto !== b.produto || a.opacidade !== b.opacidade) onChange();
    }),
});
